import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBullseye,
  faChevronLeft,
  faChevronRight,
  faPlus,
  faXmark,
  faArrowUp,
  faArrowDown,
  faArrowRight,
  faBriefcase,
  faSquareCheck,
  faCircleCheck,
  faBuilding,
  faUser,
  faTriangleExclamation,
  faCrosshairs,
  faLink,
  faUnlink,
} from "@fortawesome/free-solid-svg-icons";
import { useWorkspace } from "../context/WorkspaceContext";
import { usePlan } from "../context/PlanContext";
import api from "../api/API";
import { fetchOnePage, persistOnePage } from "../api/onePageApi";
import { fetchCiclos, fetchObjetivos } from "../api/okrApi";
import TaskModal from "../components/Modals/TaskModal";
import {
  calcularAtingimento,
  calcularVariacao,
  formatarVariacao,
} from "../utils/indicatorValue";
import "./OnePagePage.css";

// Limites que materializam o conceito "cabe em uma página".
const LIMITS = { metas: 3, indicadores: 6, projetos: 4, acoes: 5 };

const META_STATUS = [
  { value: "on_track", label: "No caminho", cls: "ok" },
  { value: "risk", label: "Em risco", cls: "risk" },
  { value: "done", label: "Concluída", cls: "done" },
];

const TRENDS = [
  { value: "up", icon: faArrowUp, cls: "up" },
  { value: "down", icon: faArrowDown, cls: "down" },
  { value: "flat", icon: faArrowRight, cls: "flat" },
];

// ─── Helpers de data ───────────────────────────────────────────────────────
const toYMD = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;

const mondayOf = (date) => {
  const d = new Date(date);
  const diff = (d.getDay() + 6) % 7; // 0 = segunda
  d.setDate(d.getDate() - diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

const addDays = (date, n) => {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
};

const shortDate = (d) =>
  `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;

const parseCollection = (response) => {
  if (Array.isArray(response?.data)) return response.data;
  if (response?.data?.status === false) return [];
  return response?.data?.records || response?.data?.data || [];
};

const nextTrend = (current) => {
  const order = ["flat", "up", "down"];
  const i = order.indexOf(current);
  return order[(i + 1) % order.length];
};

const OnePagePage = () => {
  const { selectedWorkspaceId, selectedWorkspace } = useWorkspace();
  const { temRecurso } = usePlan();
  // Objective e sempre de workspace (nunca pessoal - ver analise do OKR),
  // entao linkar meta a Objective so aparece no escopo Empresa.
  const okrDisponivel = temRecurso("okrs");

  const [scope, setScope] = useState("personal");
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));

  const [foco, setFoco] = useState("");
  const [metas, setMetas] = useState([]);
  const [indicadores, setIndicadores] = useState([]);
  const [projetoIds, setProjetoIds] = useState([]);
  const [acaoIds, setAcaoIds] = useState([]);

  const [availableProjects, setAvailableProjects] = useState([]);
  const [availableTasks, setAvailableTasks] = useState([]);
  const [objetivosDisponiveis, setObjetivosDisponiveis] = useState([]);
  const [metaLinkandoIdx, setMetaLinkandoIdx] = useState(null);

  // Dados ricos vindos do backend (join ao vivo) p/ itens linkados — fonte de
  // verdade do display, pois tarefas concluídas podem não estar na lista do picker.
  const [acoesDoc, setAcoesDoc] = useState([]);
  const [projetosDoc, setProjetosDoc] = useState([]);

  // Indicadores semeados da semana anterior, ainda nao gravados nesta semana.
  const [indHerdados, setIndHerdados] = useState(false);
  const [semanaAnterior, setSemanaAnterior] = useState(null);

  const [editingTask, setEditingTask] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  const week = toYMD(weekStart);
  const weekEnd = addDays(weekStart, 6);

  // Default: se há workspace selecionado, começa no modo Empresa.
  useEffect(() => {
    if (selectedWorkspaceId) setScope("workspace");
  }, [selectedWorkspaceId]);

  // Objectives disponiveis pra linkar uma meta - so existe no escopo Empresa
  // e so busca se o plano libera o modulo (evita um 402 gratuito pra quem
  // nao tem OKR). Um Objective pode ter mais de um ciclo no workspace, entao
  // busca todos os ciclos e achata os objetivos de cada um numa lista so.
  useEffect(() => {
    if (scope !== "workspace" || !selectedWorkspaceId || !okrDisponivel) {
      setObjetivosDisponiveis([]);
      return;
    }
    let cancelado = false;
    (async () => {
      try {
        const ciclosRes = await fetchCiclos(selectedWorkspaceId);
        const ciclos = ciclosRes.data?.data || [];
        const porCiclo = await Promise.all(ciclos.map((c) => fetchObjetivos(c.id)));
        if (cancelado) return;
        const lista = [];
        porCiclo.forEach((res, i) => {
          (res.data?.data || []).forEach((obj) => {
            lista.push({ ...obj, cicloNome: ciclos[i].nome });
          });
        });
        setObjetivosDisponiveis(lista);
      } catch (e) {
        console.error("OnePagePage: erro ao carregar Objectives", e);
      }
    })();
    return () => { cancelado = true; };
  }, [scope, selectedWorkspaceId, okrDisponivel]);

  const projectsById = useMemo(() => {
    const map = {};
    availableProjects.forEach((p) => { map[p.id] = p; });
    return map;
  }, [availableProjects]);

  const tasksById = useMemo(() => {
    const map = {};
    availableTasks.forEach((t) => { map[t.id] = t; });
    return map;
  }, [availableTasks]);

  const acoesDocById = useMemo(() => {
    const map = {};
    acoesDoc.forEach((a) => { map[a.id_tarefa] = a; });
    return map;
  }, [acoesDoc]);

  const projetosDocById = useMemo(() => {
    const map = {};
    projetosDoc.forEach((p) => { map[p.id_projeto] = p; });
    return map;
  }, [projetosDoc]);

  // Display da tarefa: prioriza dado fresco do picker, cai no join do backend.
  const getTaskInfo = (tid) => {
    const t = tasksById[tid];
    if (t) {
      return {
        descricao: t.descricao,
        concluido: t.concluido,
        vencimento: t.vencimento,
        dono: t.usuario_responsavel || t.responsavel || null,
      };
    }
    const a = acoesDocById[tid];
    if (a) {
      return {
        descricao: a.descricao,
        concluido: a.concluido,
        vencimento: a.vencimento,
        dono: a.responsavel_nome || null,
      };
    }
    return null;
  };

  const getProjInfo = (pid) => {
    const p = projectsById[pid] || projetosDocById[pid];
    if (!p) return null;
    return {
      descricao: p.descricao,
      cor: p.cor_hexa,
      total: Number(p.total_tarefas || 0),
      done: Number(p.total_concluidas || 0),
    };
  };

  // ─── Carregamento ─────────────────────────────────────────────────────────
  const loadAll = useCallback(async () => {
    if (scope === "workspace" && !selectedWorkspaceId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const projectsReq =
        scope === "workspace"
          ? api.get(`/projects?workspaceId=${selectedWorkspaceId}`)
          : api.get(`/projects`);
      const tasksReq =
        scope === "workspace"
          ? api.get(`/tasks?workspaceId=${selectedWorkspaceId}`)
          : api.get(`/tasks`);

      const [docRes, projRes, taskRes] = await Promise.all([
        fetchOnePage({ scope, workspaceId: selectedWorkspaceId, week }),
        projectsReq,
        tasksReq,
      ]);

      setAvailableProjects(parseCollection(projRes));
      setAvailableTasks(parseCollection(taskRes));

      const doc = docRes?.data?.data || {};
      setFoco(doc.foco || "");
      setMetas((doc.metas || []).map((m) => ({
        texto: m.texto,
        status: m.status,
        id_okr_objetivo: m.id_okr_objetivo || null,
      })));
      setIndicadores(
        (doc.indicadores || []).map((i) => ({
          label: i.label,
          valor: i.valor || "",
          meta: i.meta || "",
          tendencia: i.tendencia || "flat",
          valorAnterior: i.valor_anterior || null,
        }))
      );
      setIndHerdados(Boolean(doc.indicadores_herdados));
      setSemanaAnterior(doc.semana_anterior || null);
      setProjetoIds((doc.projetos || []).map((p) => p.id_projeto));
      setAcaoIds((doc.acoes || []).map((a) => a.id_tarefa));
      setProjetosDoc(doc.projetos || []);
      setAcoesDoc(doc.acoes || []);
      setSavedAt(doc.atualizado_em || null);
      setDirty(false);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Erro ao carregar a OnePage.");
    } finally {
      setLoading(false);
    }
  }, [scope, selectedWorkspaceId, week]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const markDirty = () => setDirty(true);

  // ─── Mutadores ────────────────────────────────────────────────────────────
  const updateMeta = (idx, patch) => {
    setMetas((prev) => prev.map((m, i) => (i === idx ? { ...m, ...patch } : m)));
    markDirty();
  };
  const addMeta = () => {
    if (metas.length >= LIMITS.metas) return;
    setMetas((prev) => [...prev, { texto: "", status: "on_track", id_okr_objetivo: null }]);
    markDirty();
  };
  const removeMeta = (idx) => {
    setMetas((prev) => prev.filter((_, i) => i !== idx));
    markDirty();
  };

  // Linka a meta a um Objective real - texto/status exibidos passam a vir do
  // Objective (o backend recalcula no GET); aqui so guarda o id e ja mostra
  // algo razoavel antes do proximo carregamento.
  const linkMetaAObjetivo = (idx, objetivo) => {
    setMetas((prev) => prev.map((m, i) => (i === idx
      ? {
          ...m,
          id_okr_objetivo: objetivo.id,
          texto: objetivo.titulo,
          status: objetivo.confianca === "on_track" ? "on_track" : "risk",
        }
      : m)));
    setMetaLinkandoIdx(null);
    markDirty();
  };

  // Desvincula - volta a ser texto livre, mantendo o ultimo texto exibido
  // como ponto de partida (nao apaga tudo de uma vez).
  const unlinkMeta = (idx) => {
    setMetas((prev) => prev.map((m, i) => (i === idx ? { ...m, id_okr_objetivo: null } : m)));
    markDirty();
  };

  const updateInd = (idx, patch) => {
    setIndicadores((prev) => prev.map((k, i) => (i === idx ? { ...k, ...patch } : k)));
    markDirty();
  };
  const addInd = () => {
    if (indicadores.length >= LIMITS.indicadores) return;
    setIndicadores((prev) => [
      ...prev,
      { label: "", valor: "", meta: "", tendencia: "flat", valorAnterior: null },
    ]);
    markDirty();
  };
  const removeInd = (idx) => {
    setIndicadores((prev) => prev.filter((_, i) => i !== idx));
    markDirty();
  };

  const addProjeto = (id) => {
    const pid = Number(id);
    if (!pid || projetoIds.includes(pid) || projetoIds.length >= LIMITS.projetos) return;
    setProjetoIds((prev) => [...prev, pid]);
    markDirty();
  };
  const removeProjeto = (pid) => {
    setProjetoIds((prev) => prev.filter((x) => x !== pid));
    markDirty();
  };

  const addAcao = (id) => {
    const tid = Number(id);
    if (!tid || acaoIds.includes(tid) || acaoIds.length >= LIMITS.acoes) return;
    setAcaoIds((prev) => [...prev, tid]);
    markDirty();
  };
  const removeAcao = (tid) => {
    setAcaoIds((prev) => prev.filter((x) => x !== tid));
    markDirty();
  };

  // ─── Salvar ───────────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    if (scope === "workspace" && !selectedWorkspaceId) return;
    setSaving(true);
    setError(null);
    try {
      const res = await persistOnePage({
        scope,
        workspaceId: selectedWorkspaceId,
        week,
        foco,
        // Linkada a Objective passa mesmo sem texto (o texto exibido vem do
        // Objective, gravado so como referencia) - so descarta quem nao tem
        // nem texto nem link.
        metas: metas
          .filter((m) => m.id_okr_objetivo || m.texto.trim())
          .map((m) => ({ texto: m.texto, status: m.status, idOkrObjetivo: m.id_okr_objetivo || null })),
        // valorAnterior e so referencia de tela; nao faz parte do documento.
        indicadores: indicadores
          .filter((i) => i.label.trim())
          .map(({ label, valor, meta, tendencia }) => ({ label, valor, meta, tendencia })),
        projetoIds,
        acaoIds,
      });
      setSavedAt(res?.data?.data?.atualizado_em || new Date().toISOString());
      setIndHerdados(false);
      setDirty(false);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  }, [scope, selectedWorkspaceId, week, foco, metas, indicadores, projetoIds, acaoIds]);

  // Autosave: salva sozinho ~1,2s após o usuário parar de editar.
  useEffect(() => {
    if (!dirty || loading || saving) return;
    if (scope === "workspace" && !selectedWorkspaceId) return;
    const handle = setTimeout(() => { handleSave(); }, 1200);
    return () => clearTimeout(handle);
  }, [dirty, saving, loading, scope, selectedWorkspaceId, handleSave]);

  // Abre a tarefa completa (enriquecida) no offcanvas de edição.
  const openTask = async (tid) => {
    try {
      const res = await api.get(`/tasks/${tid}`);
      if (res?.data?.id) {
        setEditingTask(res.data);
        setShowTaskModal(true);
      }
    } catch (err) {
      setError("Não foi possível abrir a tarefa.");
    }
  };

  // Após salvar a tarefa no modal: atualiza só o display (sem descartar edições
  // não salvas de foco/metas/indicadores da OnePage).
  const refreshAfterTaskSave = async () => {
    try {
      const taskReq =
        scope === "workspace"
          ? api.get(`/tasks?workspaceId=${selectedWorkspaceId}`)
          : api.get(`/tasks`);
      const [docRes, taskRes] = await Promise.all([
        fetchOnePage({ scope, workspaceId: selectedWorkspaceId, week }),
        taskReq,
      ]);
      setAvailableTasks(parseCollection(taskRes));
      const doc = docRes?.data?.data || {};
      setAcoesDoc(doc.acoes || []);
      setProjetosDoc(doc.projetos || []);
    } catch {
      /* silencioso — o modal já salvou; só o refresh de display falhou */
    }
  };

  const goWeek = (delta) => setWeekStart((prev) => addDays(mondayOf(prev), delta * 7));
  const goThisWeek = () => setWeekStart(mondayOf(new Date()));
  const isThisWeek = toYMD(mondayOf(new Date())) === week;

  const availableProjectOptions = availableProjects.filter((p) => !projetoIds.includes(p.id));
  const availableTaskOptions = availableTasks.filter(
    (t) => !acaoIds.includes(t.id) && !t.concluido
  );

  const needsWorkspace = scope === "workspace" && !selectedWorkspaceId;

  return (
    <div className="onep-page">
      <div className="onep-orb onep-orb-1" />
      <div className="onep-orb onep-orb-2" />

      {/* HEADER */}
      <header className="onep-header">
        <div className="onep-header-top">
          <div className="onep-title-wrap">
            <h1 className="onep-title">OnePage</h1>
            <span className="onep-subtitle">Tudo que importa na semana, em uma tela só.</span>
          </div>

          <div className="onep-scope-toggle">
            <button
              className={`onep-scope-btn ${scope === "workspace" ? "active" : ""}`}
              onClick={() => setScope("workspace")}
            >
              <FontAwesomeIcon icon={faBuilding} />
              {selectedWorkspace?.nome || "Empresa"}
            </button>
            <button
              className={`onep-scope-btn ${scope === "personal" ? "active" : ""}`}
              onClick={() => setScope("personal")}
            >
              <FontAwesomeIcon icon={faUser} /> Pessoal
            </button>
          </div>
        </div>

        <div className="onep-header-bottom">
          <div className="onep-week-nav">
            <button className="onep-week-btn" onClick={() => goWeek(-1)} aria-label="Semana anterior">
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <div className="onep-week-info">
              <strong>Semana {shortDate(weekStart)} – {shortDate(weekEnd)}</strong>
              {!isThisWeek && (
                <button className="onep-this-week" onClick={goThisWeek}>Voltar para esta semana</button>
              )}
            </div>
            <button className="onep-week-btn" onClick={() => goWeek(1)} aria-label="Próxima semana">
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>

          <div className="onep-save-area">
            <span className="onep-autosave">
              {(saving || dirty) ? (
                <><span className="onep-autosave-spinner" /> Salvando…</>
              ) : savedAt ? (
                <><FontAwesomeIcon icon={faCircleCheck} /> Tudo salvo</>
              ) : (
                <>Autosave ativo</>
              )}
            </span>
          </div>
        </div>
      </header>

      {error && (
        <div className="onep-error">
          <FontAwesomeIcon icon={faTriangleExclamation} /> {error}
        </div>
      )}

      {needsWorkspace ? (
        <div className="onep-empty-state">
          Selecione um workspace no topo para montar a OnePage da empresa, ou troque para o modo Pessoal.
        </div>
      ) : loading ? (
        <div className="onep-loading"><div className="onep-spinner" /> Carregando…</div>
      ) : (
        <div className="onep-body">
          {/* FOCO */}
          <section className="onep-foco">
            <div className="onep-foco-icon"><FontAwesomeIcon icon={faBullseye} /></div>
            <div className="onep-foco-content">
              <label className="onep-foco-label">Foco principal da semana</label>
              <textarea
                className="onep-foco-input"
                placeholder="Qual é o resultado mais importante desta semana?"
                value={foco}
                onChange={(e) => { setFoco(e.target.value); markDirty(); }}
                rows={2}
              />
            </div>
          </section>

          {/* METAS */}
          <section className="onep-section">
            <div className="onep-section-head">
              <h2>Prioridades críticas</h2>
              <span className="onep-counter">{metas.length}/{LIMITS.metas}</span>
            </div>
            <div className="onep-metas">
              {metas.map((m, idx) => {
                const linkada = Boolean(m.id_okr_objetivo);
                const linkando = metaLinkandoIdx === idx;
                return (
                  <div key={idx} className={`onep-meta-card ${linkada ? "onep-meta-linkada" : ""}`}>
                    {linkando ? (
                      <select
                        className="onep-meta-input"
                        autoFocus
                        defaultValue=""
                        onChange={(e) => {
                          const objetivo = objetivosDisponiveis.find((o) => String(o.id) === e.target.value);
                          if (objetivo) linkMetaAObjetivo(idx, objetivo);
                        }}
                        onBlur={() => setMetaLinkandoIdx(null)}
                      >
                        <option value="" disabled>Escolha um Objective...</option>
                        {objetivosDisponiveis.map((o) => (
                          <option key={o.id} value={o.id}>{o.cicloNome} — {o.titulo}</option>
                        ))}
                      </select>
                    ) : (
                      <div className="onep-meta-input-wrap">
                        {linkada && (
                          <FontAwesomeIcon icon={faCrosshairs} className="onep-meta-okr-badge" title="Vindo de um Objective de OKR" />
                        )}
                        <input
                          className="onep-meta-input"
                          placeholder={`Prioridade ${idx + 1}`}
                          value={m.texto}
                          disabled={linkada}
                          title={linkada ? "Texto vem do Objective vinculado - desvincule para editar" : undefined}
                          onChange={(e) => updateMeta(idx, { texto: e.target.value })}
                        />
                      </div>
                    )}
                    <div className="onep-meta-foot">
                      <div className="onep-status-pills">
                        {META_STATUS.filter((s) => !linkada || s.value !== "done").map((s) => (
                          <button
                            key={s.value}
                            className={`onep-status-pill ${s.cls} ${m.status === s.value ? "active" : ""}`}
                            disabled={linkada}
                            title={linkada ? "Confianca vem do Objective vinculado" : undefined}
                            onClick={() => updateMeta(idx, { status: s.value })}
                          >
                            {s.label}
                          </button>
                        ))}
                      </div>
                      {okrDisponivel && scope === "workspace" && (
                        linkada ? (
                          <button className="onep-remove" onClick={() => unlinkMeta(idx)} title="Desvincular do Objective">
                            <FontAwesomeIcon icon={faUnlink} />
                          </button>
                        ) : (
                          <button className="onep-remove" onClick={() => setMetaLinkandoIdx(idx)} title="Linkar a um Objective de OKR">
                            <FontAwesomeIcon icon={faLink} />
                          </button>
                        )
                      )}
                      <button className="onep-remove" onClick={() => removeMeta(idx)} aria-label="Remover">
                        <FontAwesomeIcon icon={faXmark} />
                      </button>
                    </div>
                  </div>
                );
              })}
              {metas.length < LIMITS.metas && (
                <button className="onep-add-card" onClick={addMeta}>
                  <FontAwesomeIcon icon={faPlus} /> Adicionar prioridade
                </button>
              )}
            </div>
          </section>

          <div className="onep-bottom">
            {/* INDICADORES */}
            <section className="onep-section onep-panel">
              <div className="onep-section-head">
                <h2>Indicadores</h2>
                <span className="onep-counter">{indicadores.length}/{LIMITS.indicadores}</span>
              </div>

              {indHerdados && (
                <div className="onep-ind-herdado">
                  Herdados da semana de {shortDate(new Date(`${semanaAnterior}T00:00:00`))} —
                  ajuste os valores. Só grava quando você editar algo.
                </div>
              )}

              <div className="onep-indicadores">
                {indicadores.map((k, idx) => {
                  const trend = TRENDS.find((t) => t.value === k.tendencia) || TRENDS[2];
                  const atingimento = calcularAtingimento(k.valor, k.meta);
                  const variacao = formatarVariacao(
                    calcularVariacao(k.valor, k.valorAnterior)
                  );

                  return (
                    <div key={idx} className="onep-ind-card">
                      <button
                        className={`onep-ind-trend ${trend.cls}`}
                        onClick={() => updateInd(idx, { tendencia: nextTrend(k.tendencia) })}
                        title="Tendência"
                      >
                        <FontAwesomeIcon icon={trend.icon} />
                      </button>
                      <div className="onep-ind-fields">
                        <div className="onep-ind-line">
                          <input
                            className="onep-ind-value"
                            placeholder="valor"
                            value={k.valor}
                            onChange={(e) => updateInd(idx, { valor: e.target.value })}
                          />
                          <span className="onep-ind-sep">/</span>
                          <input
                            className="onep-ind-meta"
                            placeholder="meta"
                            value={k.meta}
                            onChange={(e) => updateInd(idx, { meta: e.target.value })}
                          />
                          {atingimento !== null && (
                            /* Sem cor de propósito: o sistema ainda não sabe se,
                               neste indicador, passar da meta é bom ou ruim. */
                            <span className="onep-ind-atingimento" title="Valor sobre a meta">
                              {atingimento}%
                            </span>
                          )}
                        </div>
                        <input
                          className="onep-ind-label"
                          placeholder="indicador"
                          value={k.label}
                          onChange={(e) => updateInd(idx, { label: e.target.value })}
                        />
                        {k.valorAnterior && (
                          <span className="onep-ind-anterior">
                            semana anterior: {k.valorAnterior}
                            {variacao && variacao !== "igual" && ` · ${variacao}`}
                          </span>
                        )}
                      </div>
                      <button className="onep-remove" onClick={() => removeInd(idx)} aria-label="Remover">
                        <FontAwesomeIcon icon={faXmark} />
                      </button>
                    </div>
                  );
                })}
                {indicadores.length < LIMITS.indicadores && (
                  <button className="onep-add-card" onClick={addInd}>
                    <FontAwesomeIcon icon={faPlus} /> Adicionar indicador
                  </button>
                )}
              </div>
            </section>

            {/* PROJETOS */}
            <section className="onep-section onep-panel">
              <div className="onep-section-head">
                <h2>Projetos prioritários</h2>
                <span className="onep-counter">{projetoIds.length}/{LIMITS.projetos}</span>
              </div>
              <div className="onep-list">
                {projetoIds.map((pid) => {
                  const p = getProjInfo(pid);
                  const total = p?.total || 0;
                  const done = p?.done || 0;
                  const pct = total ? Math.round((done / total) * 100) : 0;
                  return (
                    <div key={pid} className="onep-row">
                      <span className="onep-row-dot" style={{ background: p?.cor || "#6366f1" }} />
                      <div className="onep-row-main">
                        <span className="onep-row-title">{p?.descricao || `Projeto #${pid}`}</span>
                        {total > 0 && (
                          <div className="onep-row-bar">
                            <div className="onep-row-fill" style={{ width: `${pct}%` }} />
                          </div>
                        )}
                      </div>
                      {total > 0 && <span className="onep-row-meta">{done}/{total}</span>}
                      <button className="onep-remove" onClick={() => removeProjeto(pid)} aria-label="Remover">
                        <FontAwesomeIcon icon={faXmark} />
                      </button>
                    </div>
                  );
                })}
                {projetoIds.length < LIMITS.projetos && (
                  <select
                    className="onep-picker"
                    value=""
                    onChange={(e) => addProjeto(e.target.value)}
                  >
                    <option value="">+ Adicionar projeto…</option>
                    {availableProjectOptions.map((p) => (
                      <option key={p.id} value={p.id}>{p.descricao}</option>
                    ))}
                  </select>
                )}
              </div>
            </section>

          {/* AÇÕES */}
          <section className="onep-section onep-panel">
            <div className="onep-section-head">
              <h2>Ações da semana</h2>
              <span className="onep-counter">{acaoIds.length}/{LIMITS.acoes}</span>
            </div>
            <div className="onep-list">
              {acaoIds.map((tid) => {
                const t = getTaskInfo(tid);
                const dono = t?.dono || "Sem dono";
                const prazo = t?.vencimento ? shortDate(new Date(t.vencimento)) : null;
                return (
                  <div
                    key={tid}
                    className={`onep-acao-row clickable ${t?.concluido ? "done" : ""}`}
                    onClick={() => openTask(tid)}
                    role="button"
                    title="Abrir tarefa"
                  >
                    <FontAwesomeIcon icon={faSquareCheck} className="onep-acao-check" />
                    <span className="onep-acao-title">{t?.descricao || `Tarefa #${tid}`}</span>
                    <span className="onep-acao-owner">{dono}</span>
                    {prazo && <span className="onep-acao-due">{prazo}</span>}
                    <button
                      className="onep-remove"
                      onClick={(e) => { e.stopPropagation(); removeAcao(tid); }}
                      aria-label="Remover"
                    >
                      <FontAwesomeIcon icon={faXmark} />
                    </button>
                  </div>
                );
              })}
              {acaoIds.length < LIMITS.acoes && (
                <select
                  className="onep-picker"
                  value=""
                  onChange={(e) => addAcao(e.target.value)}
                >
                  <option value="">+ Vincular ação (tarefa)…</option>
                  {availableTaskOptions.map((t) => (
                    <option key={t.id} value={t.id}>{t.descricao}</option>
                  ))}
                </select>
              )}
            </div>
            <div className="onep-acao-hint">
              <FontAwesomeIcon icon={faBriefcase} /> Cada ação puxa dono e prazo da própria tarefa — toda ação deve mover uma prioridade acima.
            </div>
          </section>
          </div>
        </div>
      )}

      <TaskModal
        show={showTaskModal}
        onClose={() => setShowTaskModal(false)}
        task={editingTask}
        offcanvas
        onSaved={refreshAfterTaskSave}
      />
    </div>
  );
};

export default OnePagePage;
